import isNull from 'lodash-es/isNull';
import isUndefined from 'lodash-es/isUndefined';
import type mdast from 'mdast';
import { createEmptyParagraph } from '../dom/paragraph';

export function markdownAstToDom(document: Document, node: mdast.Root | mdast.Paragraph): DocumentFragment;
export function markdownAstToDom(document: Document, node: mdast.Root | mdast.Content): Node | undefined;
export function markdownAstToDom(document: Document, node: mdast.Root | mdast.Content): Node | undefined {
  switch (node.type) {
    case 'root': {
      const fragment = document.createDocumentFragment();
      appendBlockContent(document, fragment, node.children);
      return fragment;
    }

    // Convert headings to paragraphs.
    case 'paragraph':
    case 'heading': {
      const fragment = document.createDocumentFragment();
      appendLineContent(document, fragment, node.children);
      return fragment;
    }

    case 'blockquote': {
      const element = document.createElement('blockquote');
      appendBlockContent(document, element, node.children);
      return element;
    }

    case 'list': {
      const list = document.createElement(node.ordered ? 'ol' : 'ul');

      // node.start can be null, contrary to the mdast typings.
      if (!isUndefined(node.start) && !isNull(node.start) && node.start !== 1) {
        list.setAttribute('start', String(node.start));
      }

      node.children.forEach(child => {
        const listItem = document.createElement('li');
        appendBlockContent(document, listItem, child.children);
        list.appendChild(listItem);
      });

      return list;
    }

    case 'html':
      return undefined;

    case 'code': {
      const preElement = document.createElement('pre');
      const codeElement = document.createElement('code');
      codeElement.textContent = node.value;
      preElement.appendChild(codeElement);
      return preElement;
    }

    case 'text':
      return document.createTextNode(node.value);

    case 'emphasis': {
      const element = document.createElement('i');
      appendPhrasingContent(document, element, node.children);
      return element;
    }

    case 'strong': {
      const element = document.createElement('b');
      appendPhrasingContent(document, element, node.children);
      return element;
    }

    case 'delete': {
      const element = document.createElement('s');
      appendPhrasingContent(document, element, node.children);
      return element;
    }

    case 'inlineCode': {
      const element = document.createElement('code');
      element.textContent = node.value;
      return element;
    }

    case 'break':
      return document.createElement('br');

    case 'link': {
      const element = document.createElement('a');
      element.href = node.url;

      if (node.title) {
        element.title = node.title;
      }

      appendPhrasingContent(document, element, node.children);
      return element;
    }

    case 'image': {
      const element = document.createElement('img');
      element.src = node.url;

      if (node.alt) {
        element.alt = node.alt;
      }

      return element;
    }

    default:
      return document.createComment(`Unrecognized Markdown AST node '${node.type}'`);
  }
}

function appendBlockContent(document: Document, parentNode: Node, children: mdast.Content[]): void {
  children.forEach(child => {
    const node = markdownAstToDom(document, child);
    if (!node) {
      return;
    }

    if (parentNode.hasChildNodes()) {
      parentNode.appendChild(createEmptyParagraph(document));
    }

    parentNode.appendChild(node);
  });

  if (!parentNode.hasChildNodes()) {
    parentNode.appendChild(document.createElement('br'));
  }
}

function appendLineContent(document: Document, parentNode: Node, children: mdast.PhrasingContent[]): void {
  let lineElement = document.createElement('div');

  children.forEach(child => {
    if (child.type === 'break') {
      if (lineElement.childNodes.length === 0) {
        lineElement.appendChild(document.createElement('br'));
      }

      parentNode.appendChild(lineElement);
      lineElement = document.createElement('div');
      return;
    }

    const node = markdownAstToDom(document, child);
    if (!node) {
      return;
    }

    lineElement.appendChild(node);
  });

  if (lineElement.childNodes.length > 0) {
    parentNode.appendChild(lineElement);
  }
}

function appendPhrasingContent(
  document: Document,
  parentNode: Node,
  children: mdast.PhrasingContent[],
): void {
  children.forEach(child => {
    const node = markdownAstToDom(document, child);
    if (!node) {
      return;
    }

    parentNode.appendChild(node);
  });
}
